import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { MSG, NAV_LINKS, wa } from "../../data/site";
import { LogoFull, WhatsAppIcon } from "./Icons";

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <header
            data-testid="main-header"
            className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${
                scrolled
                    ? "border-b border-slate-200/70 bg-white/85 shadow-sm backdrop-blur-md"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <a href="#inicio" aria-label="Ir para o início" data-testid="header-logo-link">
                    <LogoFull />
                </a>
                <nav className="hidden items-center gap-7 lg:flex" aria-label="Navegação principal">
                    {NAV_LINKS.map((link) => (
                        <a
                            key={link.href}
                            href={link.href}
                            data-testid={`nav-link-${link.href.slice(1)}`}
                            className="text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-[#1A6AFF]"
                        >
                            {link.label}
                        </a>
                    ))}
                </nav>
                <div className="flex items-center gap-3">
                    <a
                        href={wa(MSG.consulta)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="header-schedule-button"
                        className="hidden items-center gap-2 rounded-full bg-[#1A6AFF] px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#0052E0] sm:inline-flex"
                    >
                        <WhatsAppIcon className="h-4 w-4" />
                        Agendar consulta
                    </a>
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        aria-label="Abrir menu"
                        data-testid="mobile-menu-button"
                        className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-800 lg:hidden"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        className="fixed inset-0 z-50 lg:hidden"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        data-testid="mobile-menu"
                    >
                        <div
                            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
                            onClick={() => setOpen(false)}
                        />
                        <motion.div
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            className="absolute right-0 top-0 flex h-full w-80 max-w-[85vw] flex-col bg-white p-6 shadow-2xl"
                        >
                            <div className="flex items-center justify-between">
                                <LogoFull />
                                <button
                                    type="button"
                                    onClick={() => setOpen(false)}
                                    aria-label="Fechar menu"
                                    data-testid="mobile-menu-close-button"
                                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-700"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            <nav className="mt-10 flex flex-col gap-1" aria-label="Menu móvel">
                                {NAV_LINKS.map((link, i) => (
                                    <motion.a
                                        key={link.href}
                                        href={link.href}
                                        onClick={() => setOpen(false)}
                                        data-testid={`mobile-nav-link-${link.href.slice(1)}`}
                                        initial={{ opacity: 0, x: 24 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.08 + i * 0.05 }}
                                        className="rounded-xl px-4 py-3 font-heading text-lg font-semibold text-slate-800 transition-colors hover:bg-[#EFF5FF] hover:text-[#1A6AFF]"
                                    >
                                        {link.label}
                                    </motion.a>
                                ))}
                            </nav>
                            <a
                                href={wa(MSG.consulta)}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="mobile-schedule-button"
                                className="mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#1A6AFF] px-6 py-4 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-colors hover:bg-[#0052E0]"
                            >
                                <WhatsAppIcon className="h-5 w-5" />
                                Agendar consulta
                            </a>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};
